import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Search,
  Loader2,
  Mail,
  ShieldCheck,
  Crown,
  CreditCard,
  Users,
  ArrowLeft,
  ArrowRight,
  Send,
  CheckCircle2,
  XCircle,
  Gift,
  AlertTriangle,
  Clock,
  LayoutDashboard,
  TrendingUp,
  DollarSign,
  Trash2,
  FlaskConical,
  ChevronLeft,
  ChevronRight,
  List,
  PiggyBank,
  Eye,
  MousePointerClick,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const { token } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const resultRef = useRef(null);
  const [actionLoading, setActionLoading] = useState('');
  const [auditLog, setAuditLog] = useState(null);
  const [showAudit, setShowAudit] = useState(false);

  // Dashboard stats
  const [dashboard, setDashboard] = useState(null);

  // Users list
  const [showUsersList, setShowUsersList] = useState(false);
  const [usersList, setUsersList] = useState(null);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [usersListLoading, setUsersListLoading] = useState(false);

  // Delete user (danger zone)
  const [showDeleteUserConfirm, setShowDeleteUserConfirm] = useState(false);
  const [deleteUserConfirmText, setDeleteUserConfirmText] = useState('');

  // Adjust referral form
  const [creditDays, setCreditDays] = useState('');
  const [debtDays, setDebtDays] = useState('');
  const [bonusDays, setBonusDays] = useState('');

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const res = await fetch(`${API}/api/admin/dashboard`, { headers });
      if (res.ok) setDashboard(await res.json());
    } catch { /* non-blocking */ }
  };

  const loadUsersList = async (page = 1) => {
    setUsersListLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/users?page=${page}&page_size=20`, { headers });
      if (res.ok) {
        const data = await res.json();
        setUsersList(data.users);
        setUsersPage(data.page);
        setUsersTotalPages(data.total_pages);
        setShowUsersList(true);
      } else {
        toast.error('Error al cargar usuarios');
      }
    } catch {
      toast.error('Error de conexión');
    } finally {
      setUsersListLoading(false);
    }
  };

  const searchUser = async (e, directEmail) => {
    if (e) e.preventDefault();
    const query = directEmail || searchQuery.trim();
    if (!query) return;
    setLoading(true);
    setUserData(null);
    setCandidates(null);
    setShowDeleteUserConfirm(false);
    setDeleteUserConfirmText('');
    try {
      const res = await fetch(`${API}/api/admin/search-user?q=${encodeURIComponent(query)}`, { headers });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.detail || 'Usuario no encontrado');
        return;
      }
      const data = await res.json();
      if (data.multiple) {
        setCandidates(data.candidates);
      } else {
        setUserData(data);
        setCreditDays(String(data.referral_stats.credit_days));
        setDebtDays(String(data.referral_stats.debt_days));
        setBonusDays(String(data.referral_stats.bonus_days));
        // El resultado vive más abajo que la lista/búsqueda que lo dispara; sin este scroll,
        // quien hace clic en una fila de "Todos los usuarios" no ve ningún cambio en pantalla
        // y parece que el clic no hizo nada. requestAnimationFrame espera a que React monte
        // el bloque de resultado (antes de este setUserData, `u` era null y el nodo no existía).
        requestAnimationFrame(() => {
          resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    } catch {
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const selectCandidate = (email) => {
    setSearchQuery(email);
    setCandidates(null);
    searchUser(null, email);
  };

  const loadAuditLog = async () => {
    try {
      const res = await fetch(`${API}/api/admin/audit-log?limit=50`, { headers });
      if (res.ok) {
        const data = await res.json();
        setAuditLog(data.logs);
        setShowAudit(true);
      }
    } catch {
      toast.error('Error cargando log');
    }
  };

  const adminAction = async (endpoint, body, successMsg) => {
    setActionLoading(endpoint);
    try {
      const res = await fetch(`${API}/api/admin/${endpoint}`, {
        method: 'POST', headers, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Error'); return; }
      toast.success(successMsg || data.message);
      // Refresh user data
      if (userData?.user?.email) {
        const r = await fetch(`${API}/api/admin/search-user?q=${encodeURIComponent(userData.user.email)}`, { headers });
        if (r.ok) {
          const d = await r.json();
          if (!d.multiple) setUserData(d);
        }
      }
      loadDashboard();
    } catch {
      toast.error('Error de conexión');
    } finally {
      setActionLoading('');
    }
  };

  const deleteUser = async () => {
    if (!u || deleteUserConfirmText !== u.email) return;
    setActionLoading('delete-user');
    try {
      const res = await fetch(`${API}/api/admin/delete-user`, {
        method: 'POST', headers, body: JSON.stringify({ email: u.email, confirm_email: deleteUserConfirmText }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Error al eliminar'); return; }
      toast.success(data.message || 'Usuario eliminado');
      setUserData(null);
      setCandidates(null);
      setSearchQuery('');
      setShowDeleteUserConfirm(false);
      setDeleteUserConfirmText('');
      loadDashboard();
      if (showUsersList) loadUsersList(usersPage);
    } catch {
      toast.error('Error de conexión');
    } finally {
      setActionLoading('');
    }
  };

  const u = userData?.user;
  const rs = userData?.referral_stats;
  const ss = userData?.stripe_subscription;
  const activity = userData?.activity;
  const totalPaidMxn = userData?.total_paid_mxn;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b">
        <div className="max-w-6xl mx-auto px-4 md:px-8 flex h-14 items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-muted-foreground hover:text-slate-900">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-bold text-lg font-['Manrope']">Panel Admin</h1>
            <Badge variant="outline" className="text-xs">Solo admin</Badge>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => loadUsersList(1)} data-testid="admin-users-list-btn" className="gap-1.5 text-muted-foreground">
              <List className="w-4 h-4" /> Todos los usuarios
            </Button>
            <Button variant="ghost" size="sm" onClick={loadAuditLog} data-testid="admin-audit-btn" className="gap-1.5 text-muted-foreground">
              <Clock className="w-4 h-4" /> Log de acciones
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8 space-y-6">
        {/* Dashboard Stats */}
        {dashboard && (
          <Card data-testid="admin-dashboard-stats">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <LayoutDashboard className="w-5 h-5" /> Resumen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Stat label="Usuarios totales" value={dashboard.total_users} icon={<Users className="w-4 h-4 text-slate-500" />} />
                <Stat label="Usuarios Pro" value={dashboard.pro_users} icon={<Crown className="w-4 h-4 text-teal-600" />} />
                <Stat label="Tasa de conversión" value={`${dashboard.conversion_rate}%`} icon={<TrendingUp className="w-4 h-4 text-[#2563EB]" />} />
                <Stat label="Ingresos totales" value={`$${dashboard.total_revenue_mxn.toLocaleString('es-MX')} MXN`} icon={<DollarSign className="w-4 h-4 text-green-600" />} />
                <Stat label="Ingresos este mes" value={`$${dashboard.revenue_this_month_mxn.toLocaleString('es-MX')} MXN`} />
                <Stat label="Nuevos este mes" value={dashboard.new_users_this_month} />
                <Stat label="Usuarios gratis" value={dashboard.free_users} />
                <Stat label="De prueba/inactivos" value={dashboard.test_users} />
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Excluye usuarios marcados como de prueba/inactivos.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Retirement Module Stats */}
        {dashboard && (
          <Card data-testid="admin-retirement-stats">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <PiggyBank className="w-5 h-5" /> Planeación de Retiro
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <Stat label="Diagnósticos completados" value={dashboard.retirement_diagnosticos_completados} icon={<PiggyBank className="w-4 h-4 text-indigo-600" />} />
                <Stat label="Vistas al upsell de Pro" value={dashboard.retirement_upsell_views} icon={<Eye className="w-4 h-4 text-slate-500" />} />
                <Stat label="Clics en CTA de gestoría" value={dashboard.retirement_gestoria_clicks} icon={<MousePointerClick className="w-4 h-4 text-[#2563EB]" />} />
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Excluye usuarios marcados como de prueba/inactivos.
              </p>
            </CardContent>
          </Card>
        )}

        {/* All Users List */}
        {showUsersList && (
          <Card data-testid="admin-users-list">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <List className="w-4 h-4" /> Todos los usuarios
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowUsersList(false)}>Cerrar</Button>
            </CardHeader>
            <CardContent>
              {usersListLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
              ) : (
                <>
                  <div className="space-y-2">
                    {usersList?.map((c, i) => (
                      <button
                        key={c.user_id || i}
                        onClick={() => selectCandidate(c.email)}
                        className="w-full text-left p-3 rounded-lg border hover:bg-slate-50 transition-colors flex items-center justify-between"
                        data-testid={`admin-user-row-${i}`}
                      >
                        <div>
                          <p className="font-medium text-sm">{c.name || '—'}</p>
                          <p className="text-xs text-muted-foreground">{c.email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {c.is_test && <Badge variant="outline" className="text-xs border-amber-300 text-amber-700">Prueba</Badge>}
                          <Badge className={c.plan === 'pro' ? 'bg-teal-600' : 'bg-slate-500'}>{c.plan?.toUpperCase() || 'FREE'}</Badge>
                          <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <Button
                      variant="outline" size="sm"
                      disabled={usersPage <= 1}
                      onClick={() => loadUsersList(usersPage - 1)}
                      data-testid="admin-users-prev-btn"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
                    </Button>
                    <span className="text-xs text-muted-foreground">Página {usersPage} de {usersTotalPages}</span>
                    <Button
                      variant="outline" size="sm"
                      disabled={usersPage >= usersTotalPages}
                      onClick={() => loadUsersList(usersPage + 1)}
                      data-testid="admin-users-next-btn"
                    >
                      Siguiente <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Search */}
        <Card data-testid="admin-search-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Search className="w-5 h-5" /> Buscar usuario
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={searchUser} className="flex gap-3">
              <Input
                placeholder="Correo, nombre o búsqueda parcial (mín. 3 caracteres)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1"
                data-testid="admin-search-input"
              />
              <Button type="submit" disabled={loading} data-testid="admin-search-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
                Buscar
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Candidates list (partial search results) */}
        {candidates && (
          <Card data-testid="admin-candidates-list">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-4 h-4" /> {candidates.length} resultado{candidates.length !== 1 ? 's' : ''} encontrado{candidates.length !== 1 ? 's' : ''}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {candidates.map((c, i) => (
                  <button
                    key={i}
                    onClick={() => selectCandidate(c.email)}
                    className="w-full text-left p-3 rounded-lg border hover:bg-slate-50 transition-colors flex items-center justify-between"
                    data-testid={`admin-candidate-${i}`}
                  >
                    <div>
                      <p className="font-medium text-sm">{c.name || '—'}</p>
                      <p className="text-xs text-muted-foreground">{c.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={c.plan === 'pro' ? 'bg-teal-600' : 'bg-slate-500'}>{c.plan?.toUpperCase()}</Badge>
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Audit Log Modal */}
        {showAudit && auditLog && (
          <Card data-testid="admin-audit-log">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="w-4 h-4" /> Log de acciones ({auditLog.length})
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowAudit(false)}>Cerrar</Button>
            </CardHeader>
            <CardContent>
              {auditLog.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Sin acciones registradas</p>
              ) : (
                <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white">
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="py-2 pr-4">Fecha</th>
                        <th className="py-2 pr-4">Acción</th>
                        <th className="py-2 pr-4">Usuario objetivo</th>
                        <th className="py-2">Detalles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLog.map((log, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-2 pr-4 whitespace-nowrap text-xs">{new Date(log.timestamp).toLocaleString('es-MX')}</td>
                          <td className="py-2 pr-4">
                            <Badge variant="outline" className="text-xs">{log.action}</Badge>
                          </td>
                          <td className="py-2 pr-4 text-xs">{log.target_email}</td>
                          <td className="py-2 font-mono text-xs">{log.details && Object.keys(log.details).length > 0 ? JSON.stringify(log.details) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {u && (
          <>
            {/* User Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" ref={resultRef}>
              <Card data-testid="admin-user-info">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Información del usuario
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <Row label="Nombre" value={u.name} />
                  <Row label="Email" value={u.email} />
                  <Row label="Registro" value={new Date(u.created_at).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })} />
                  <Row label="Plan" value={
                    <Badge className={u.plan === 'pro' ? 'bg-teal-600' : 'bg-slate-500'}>{u.plan?.toUpperCase()}</Badge>
                  } />
                  <Row label="Tipo" value={u.plan_type || 'N/A'} />
                  <Row label="Email verificado" value={
                    u.email_verified
                      ? <span className="text-green-600 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Sí</span>
                      : <span className="text-red-500 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> No</span>
                  } />
                  <Row label="User ID" value={<span className="font-mono text-xs">{u.user_id}</span>} />
                </CardContent>
              </Card>

              {/* Stripe */}
              <Card data-testid="admin-stripe-info">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <CreditCard className="w-4 h-4" /> Stripe
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <Row label="Customer ID" value={u.stripe_customer_id ? <span className="font-mono text-xs">{u.stripe_customer_id}</span> : 'Sin customer'} />
                  <Row label="Total pagado" value={`$${(totalPaidMxn || 0).toLocaleString('es-MX')} MXN`} />
                  <p className="text-xs text-muted-foreground -mt-1" data-testid="admin-total-paid-disclaimer">
                    No descuenta reembolsos posteriores.
                  </p>
                  {ss ? (
                    ss.error ? (
                      <p className="text-red-500 text-xs">{ss.error}</p>
                    ) : (
                      <>
                        <Row label="Suscripción" value={<span className="font-mono text-xs">{ss.id}</span>} />
                        <Row label="Estado" value={
                          <Badge className={ss.status === 'active' ? 'bg-green-600' : 'bg-amber-500'}>{ss.status}</Badge>
                        } />
                        <Row label="Período termina" value={ss.current_period_end ? new Date(ss.current_period_end * 1000).toLocaleDateString('es-MX') : 'N/A'} />
                        <Row label="Cancela al final" value={ss.cancel_at_period_end ? 'Sí' : 'No'} />
                      </>
                    )
                  ) : (
                    <p className="text-muted-foreground text-xs">Sin suscripción activa en Stripe</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Activity */}
            <Card data-testid="admin-activity-info">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Actividad
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {activity ? (
                  <>
                    <Row label="Último ingreso" value={new Date(activity.last_login_at).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })} />
                    <Row label="Duración de esa sesión" value={formatApproxDuration(activity.session_duration_seconds)} />
                    <p className="text-xs text-muted-foreground" data-testid="admin-activity-disclaimer">
                      Estimado por actividad en el servidor (no es tiempo exacto de pantalla encendida).
                    </p>
                  </>
                ) : (
                  <p className="text-muted-foreground text-xs">Sin actividad registrada todavía.</p>
                )}
              </CardContent>
            </Card>

            {/* Referral Stats */}
            <Card data-testid="admin-referral-info">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Gift className="w-4 h-4" /> Referidos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  <Stat label="Código" value={rs.code || '—'} />
                  <Stat label="Referidos totales" value={rs.total_referrals} />
                  <Stat label="Mensuales activos" value={rs.active_monthly} />
                  <Stat label="Descuento %" value={`${rs.discount_pct}%`} />
                  <Stat label="Crédito (días)" value={rs.credit_days} />
                  <Stat label="Bonus (días)" value={rs.bonus_days} />
                  <Stat label="Deuda (días)" value={rs.debt_days} highlight={rs.debt_days > 0} />
                  <Stat label="Excess credits" value={rs.excess_credits} />
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Quick Actions */}
              <Card data-testid="admin-actions">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4" /> Acciones rápidas
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                    disabled={actionLoading === 'send-reset-email'}
                    onClick={() => adminAction('send-reset-email', { email: u.email }, `Correo de reset enviado a ${u.email}`)}
                    data-testid="admin-btn-reset-password"
                  >
                    {actionLoading === 'send-reset-email' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Enviar correo de restablecimiento de contraseña
                  </Button>

                  {!u.email_verified && (
                    <Button
                      variant="outline"
                      className="w-full justify-start gap-2 border-green-200 text-green-700 hover:bg-green-50"
                      disabled={actionLoading === 'verify-email'}
                      onClick={() => adminAction('verify-email', { email: u.email }, `Email de ${u.email} verificado`)}
                      data-testid="admin-btn-verify-email"
                    >
                      {actionLoading === 'verify-email' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                      Marcar email como verificado
                    </Button>
                  )}

                  <div className="flex gap-2">
                    {u.plan !== 'pro' ? (
                      <Button
                        className="flex-1 bg-teal-600 hover:bg-teal-700 gap-2"
                        disabled={actionLoading === 'set-plan'}
                        onClick={() => adminAction('set-plan', { email: u.email, plan: 'pro' }, `${u.email} ahora es Pro`)}
                        data-testid="admin-btn-grant-pro"
                      >
                        {actionLoading === 'set-plan' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                        Otorgar Pro
                      </Button>
                    ) : (
                      <Button
                        variant="destructive"
                        className="flex-1 gap-2"
                        disabled={actionLoading === 'set-plan'}
                        onClick={() => adminAction('set-plan', { email: u.email, plan: 'free' }, `${u.email} ahora es Free`)}
                        data-testid="admin-btn-revoke-pro"
                      >
                        {actionLoading === 'set-plan' ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                        Revocar Pro
                      </Button>
                    )}
                  </div>

                  <Button
                    variant="outline"
                    className={`w-full justify-start gap-2 ${u.is_test ? 'border-amber-300 text-amber-700 hover:bg-amber-50' : ''}`}
                    disabled={actionLoading === 'mark-test'}
                    onClick={() => adminAction('mark-test', { email: u.email, is_test: !u.is_test }, u.is_test ? `${u.email} ya no es de prueba` : `${u.email} marcado como de prueba/inactivo`)}
                    data-testid="admin-btn-toggle-test"
                  >
                    {actionLoading === 'mark-test' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
                    {u.is_test ? 'Quitar marca de prueba/inactivo' : 'Marcar como de prueba/inactivo'}
                  </Button>
                  {u.is_test && (
                    <p className="text-xs text-amber-700">Este usuario está excluido de las métricas del dashboard.</p>
                  )}
                </CardContent>
              </Card>

              {/* Adjust Referrals */}
              <Card data-testid="admin-adjust-referral">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="w-4 h-4" /> Ajustar referidos
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-muted-foreground">Crédito (días)</label>
                      <Input type="number" value={creditDays} onChange={(e) => setCreditDays(e.target.value)} data-testid="admin-input-credit" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Deuda (días)</label>
                      <Input type="number" value={debtDays} onChange={(e) => setDebtDays(e.target.value)} data-testid="admin-input-debt" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Bonus (días)</label>
                      <Input type="number" value={bonusDays} onChange={(e) => setBonusDays(e.target.value)} data-testid="admin-input-bonus" />
                    </div>
                  </div>
                  <Button
                    className="w-full gap-2"
                    disabled={actionLoading === 'adjust-referral'}
                    onClick={() => adminAction('adjust-referral', {
                      email: u.email,
                      credit_days: parseInt(creditDays) || 0,
                      debt_days: parseInt(debtDays) || 0,
                      bonus_days: parseInt(bonusDays) || 0,
                    }, 'Referidos ajustados')}
                    data-testid="admin-btn-adjust"
                  >
                    {actionLoading === 'adjust-referral' ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
                    Aplicar ajuste
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Danger Zone - Delete User */}
            <Card className="border-red-200 bg-red-50/30" data-testid="admin-danger-zone">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-red-700">
                  <Trash2 className="w-4 h-4" /> Zona de peligro
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!showDeleteUserConfirm ? (
                  <Button
                    variant="outline"
                    className="border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800"
                    onClick={() => setShowDeleteUserConfirm(true)}
                    data-testid="admin-delete-user-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar este usuario permanentemente
                  </Button>
                ) : (
                  <div className="space-y-3" data-testid="admin-delete-confirm-section">
                    <p className="text-sm text-red-700 font-medium">
                      Esta acción elimina la cuenta y todos sus datos de forma permanente. Escribe <span className="font-bold">{u.email}</span> para confirmar:
                    </p>
                    <Input
                      value={deleteUserConfirmText}
                      onChange={(e) => setDeleteUserConfirmText(e.target.value)}
                      placeholder={u.email}
                      className="max-w-sm border-red-300 focus-visible:ring-red-400"
                      data-testid="admin-delete-confirm-input"
                    />
                    <div className="flex gap-3">
                      <Button
                        variant="destructive"
                        onClick={deleteUser}
                        disabled={deleteUserConfirmText !== u.email || actionLoading === 'delete-user'}
                        data-testid="admin-delete-confirm-btn"
                      >
                        {actionLoading === 'delete-user' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                        Eliminar permanentemente
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => { setShowDeleteUserConfirm(false); setDeleteUserConfirmText(''); }}
                        data-testid="admin-delete-cancel-btn"
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payment History */}
            {userData.payments?.length > 0 && (
              <Card data-testid="admin-payment-history">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <CreditCard className="w-4 h-4" /> Historial de pagos ({userData.payments.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-muted-foreground">
                          <th className="py-2 pr-4">Fecha</th>
                          <th className="py-2 pr-4">Monto</th>
                          <th className="py-2 pr-4">Plan</th>
                          <th className="py-2 pr-4">Estado</th>
                          <th className="py-2">Session ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userData.payments.map((p, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="py-2 pr-4">{p.created_at ? new Date(p.created_at).toLocaleDateString('es-MX') : '—'}</td>
                            <td className="py-2 pr-4">${p.amount_total ? (p.amount_total / 100).toFixed(2) : '—'} MXN</td>
                            <td className="py-2 pr-4">{p.plan_type || '—'}</td>
                            <td className="py-2 pr-4">
                              <Badge variant="outline" className={p.payment_status === 'paid' ? 'border-green-300 text-green-700' : ''}>
                                {p.payment_status || '—'}
                              </Badge>
                            </td>
                            <td className="py-2 font-mono text-xs truncate max-w-[200px]">{p.session_id || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function formatApproxDuration(seconds) {
  if (seconds === null || seconds === undefined) return null;
  const minutes = Math.round(seconds / 60);
  if (minutes < 1) return '< 1 min';
  if (minutes < 60) return `≈ ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `≈ ${hours}h ${mins}min` : `≈ ${hours}h`;
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-center py-1 border-b border-dashed last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}

function Stat({ label, value, highlight, icon }) {
  return (
    <div className={`rounded-lg p-3 text-center ${highlight ? 'bg-red-50 border border-red-200' : 'bg-slate-100'}`}>
      {icon && <div className="flex justify-center mb-1">{icon}</div>}
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-lg font-bold ${highlight ? 'text-red-600' : 'text-slate-900'}`}>{value}</p>
    </div>
  );
}
